import { useQuery, keepPreviousData } from '@tanstack/react-query';
import analyticsService from '../lib/analytics.service';
import type { FleetEventType } from '../types';

export const analyticsKeys = {
  all: ['analytics'] as const,
  versionDistribution: (type: string) => [...analyticsKeys.all, 'version-distribution', type] as const,
  fleetUptime: (period: string) => [...analyticsKeys.all, 'fleet-uptime', period] as const,
  fleetHeartbeat: (period: string) => [...analyticsKeys.all, 'fleet-heartbeat', period] as const,
  fleetTrends: (period: string) => [...analyticsKeys.all, 'fleet-trends', period] as const,
  fleetEvents: (type: string, period: string) => [...analyticsKeys.all, 'fleet-events', type, period] as const,
  fleetHealth: (period: string) => [...analyticsKeys.all, 'fleet-health', period] as const,
  enrollmentGrowth: (weeks: number) => [...analyticsKeys.all, 'enrollment-growth', weeks] as const,
  deviceUptime: (deviceId: string, start: string, end: string) =>
    [...analyticsKeys.all, 'device-uptime', deviceId, start, end] as const,
};

// Trend/health queries are the expensive ones (fleet-wide GROUP BYs) — a
// 60s staleTime keeps panel toggling/navigation from re-running them.
const FLEET_STALE_MS = 60 * 1000;

export function useVersionDistribution(type = 'agent') {
  return useQuery({
    queryKey: analyticsKeys.versionDistribution(type),
    queryFn: () => analyticsService.getVersionDistribution(type),
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useFleetUptime(period = '7d') {
  return useQuery({
    queryKey: analyticsKeys.fleetUptime(period),
    queryFn: () => analyticsService.getFleetUptime(period),
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useFleetHeartbeat(period = '24h') {
  return useQuery({
    queryKey: analyticsKeys.fleetHeartbeat(period),
    queryFn: () => analyticsService.getFleetHeartbeat(period),
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

// keepPreviousData: period/metric toggles keep the old chart visible during
// the refetch instead of unmounting into a spinner.
export function useFleetTrends(period = '24h') {
  return useQuery({
    queryKey: analyticsKeys.fleetTrends(period),
    queryFn: () => analyticsService.getFleetTrends(period),
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useFleetEvents(type: FleetEventType = 'boot', period = '24h') {
  return useQuery({
    queryKey: analyticsKeys.fleetEvents(type, period),
    queryFn: () => analyticsService.getFleetEvents(type, period),
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useFleetHealth(period = '24h') {
  return useQuery({
    queryKey: analyticsKeys.fleetHealth(period),
    queryFn: () => analyticsService.getFleetHealth(period),
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useEnrollmentGrowth(weeks = 12) {
  return useQuery({
    queryKey: analyticsKeys.enrollmentGrowth(weeks),
    queryFn: () => analyticsService.getEnrollmentGrowth(weeks),
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useDeviceUptime(deviceId: string, start?: string, end?: string) {
  return useQuery({
    queryKey: analyticsKeys.deviceUptime(deviceId, start ?? '', end ?? ''),
    queryFn: () => analyticsService.getDeviceUptime(deviceId, start, end),
    enabled: !!deviceId,
    staleTime: FLEET_STALE_MS,
    placeholderData: keepPreviousData,
  });
}
